import { useState, useCallback } from 'react';
import { useFormValidation, ValidationErrors, FormValidationConfig } from './formValidation';

export interface UseFormOptions<T> {
  initialValues: T;
  validationConfig: FormValidationConfig;
  onSubmit: (values: T) => Promise<void> | void;
  onError?: (errors: ValidationErrors) => void;
  validationContext?: any;
}

export interface UseFormReturn<T> {
  values: T;
  errors: ValidationErrors;
  isSubmitting: boolean;
  isValid: boolean;
  setValue: (field: keyof T, value: any) => void;
  setValues: (values: Partial<T>) => void;
  setError: (field: keyof T, error: string) => void;
  setErrors: (errors: ValidationErrors) => void;
  clearErrors: () => void;
  clearFieldError: (field: keyof T) => void;
  handleChange: (field: keyof T) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  handleSelectChange: (field: keyof T) => (event: any) => void;
  handleSubmit: (event?: React.FormEvent) => Promise<void>;
  reset: () => void;
  validateField: (field: keyof T) => void;
  validateForm: () => boolean;
  updateValidationContext: (context: any) => void;
}

export const useForm = <T extends Record<string, any>>({
  initialValues,
  validationConfig,
  onSubmit,
  onError,
  validationContext,
}: UseFormOptions<T>): UseFormReturn<T> => {
  const [values, setValues] = useState<T>(initialValues);
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [touched, setTouched] = useState<Set<keyof T>>(new Set());
  const [currentValidationContext, setCurrentValidationContext] = useState(validationContext);

  const {
    validateForm: validateFormData,
    validateFieldOnChange,
    clearFieldError: clearFieldErrorUtil,
    hasErrors,
  } = useFormValidation();

  const setValue = useCallback((field: keyof T, value: any) => {
    setValues(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (errors[field as string]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field as string];
        return newErrors;
      });
    }

    // Mark field as touched
    setTouched(prev => new Set(prev).add(field));
  }, [errors]);

  const setValuesBulk = useCallback((newValues: Partial<T>) => {
    setValues(prev => ({ ...prev, ...newValues }));
  }, []);

  const setError = useCallback((field: keyof T, error: string) => {
    setErrors(prev => ({ ...prev, [field as string]: error }));
  }, []);

  const setErrorsBulk = useCallback((newErrors: ValidationErrors) => {
    setErrors(newErrors);
  }, []);

  const clearErrors = useCallback(() => {
    setErrors({});
  }, []);

  const clearFieldError = useCallback((field: keyof T) => {
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[field as string];
      return newErrors;
    });
  }, []);

  const handleChange = useCallback((field: keyof T) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { value, type } = event.target;
    let processedValue: any = value;

    // Handle different input types
    if (type === 'number') {
      processedValue = value === '' ? '' : Number(value);
    } else if (type === 'checkbox') {
      processedValue = (event.target as HTMLInputElement).checked;
    }

    setValue(field, processedValue);

    // Validate field on change if it's been touched
    if (touched.has(field)) {
      const fieldRules = validationConfig[field as string];
      if (fieldRules) {
        const error = validateFieldOnChange(processedValue, fieldRules, field as string, values, currentValidationContext);
        if (error) {
          setError(field, error);
        } else {
          clearFieldError(field);
        }
      }
    }
  }, [setValue, touched, validationConfig, validateFieldOnChange, setError, clearFieldError]);

  const handleSelectChange = useCallback((field: keyof T) => (event: any) => {
    const value = event.target.value;
    setValue(field, value);

    // Validate field on change if it's been touched
    if (touched.has(field)) {
      const fieldRules = validationConfig[field as string];
      if (fieldRules) {
        const error = validateFieldOnChange(value, fieldRules, field as string, values, currentValidationContext);
        if (error) {
          setError(field, error);
        } else {
          clearFieldError(field);
        }
      }
    }
  }, [setValue, touched, validationConfig, validateFieldOnChange, setError, clearFieldError]);

  const validateField = useCallback((field: keyof T) => {
    const fieldRules = validationConfig[field as string];
    if (fieldRules) {
      const error = validateFieldOnChange(values[field], fieldRules, field as string, values, currentValidationContext);
      if (error) {
        setError(field, error);
        return false;
      } else {
        clearFieldError(field);
        return true;
      }
    }
    return true;
  }, [values, validationConfig, validateFieldOnChange, setError, clearFieldError]);

  const validateForm = useCallback(() => {
    const newErrors = validateFormData(values, validationConfig, currentValidationContext);
    setErrors(newErrors);
    return !hasErrors(newErrors);
  }, [values, validationConfig, validateFormData, hasErrors, currentValidationContext]);

  const handleSubmit = useCallback(async (event?: React.FormEvent) => {
    if (event) {
      event.preventDefault();
    }

    // Mark all fields as touched
    const allFields = Object.keys(validationConfig) as (keyof T)[];
    setTouched(new Set(allFields));

    // Validate form
    if (!validateForm()) {
      if (onError) {
        onError(errors);
      }
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit(values);
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  }, [validateForm, errors, onSubmit, onError, validationConfig]);

  const reset = useCallback(() => {
    setValues(initialValues);
    setErrors({});
    setTouched(new Set());
    setIsSubmitting(false);
  }, [initialValues]);

  const updateValidationContext = useCallback((context: any) => {
    setCurrentValidationContext(context);
  }, []);

  return {
    values,
    errors,
    isSubmitting,
    isValid: !hasErrors(errors),
    setValue,
    setValues: setValuesBulk,
    setError,
    setErrors: setErrorsBulk,
    clearErrors,
    clearFieldError,
    handleChange,
    handleSelectChange,
    handleSubmit,
    reset,
    validateField,
    validateForm,
    updateValidationContext,
  };
}; 